/**
 * Sidebar Navigation Component for Module Slides
 * 
 * Displays all slides in a module with navigation and progress tracking
 */

import { Slide } from '../../types/elearning';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';

interface ModuleSlideNavigationProps {
  slides: Slide[];
  currentSlideIndex: number;
  viewedSlides: Set<string>;
  onSlideSelect: (index: number) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export default function ModuleSlideNavigation({
  slides,
  currentSlideIndex,
  viewedSlides,
  onSlideSelect,
  isCollapsed = false,
  onToggleCollapse,
}: ModuleSlideNavigationProps) {
  const sortedSlides = [...slides].sort((a, b) => a.order - b.order);

  return (
    <div className="bg-white border-r border-gray-200 h-full flex flex-col w-64 shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Module Navigation</h3>
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronRightIcon
              className={`h-4 w-4 text-gray-600 transition-transform ${
                isCollapsed ? 'rotate-180' : ''
              }`}
            />
          </button>
        )}
      </div>

      {/* Slide List */}
      <div className="flex-1 overflow-y-auto">
        <nav className="p-2 space-y-1">
          {sortedSlides.map((slide, index) => {
            const isCurrent = index === currentSlideIndex;
            const isViewed = viewedSlides.has(slide.id);
            const slideTitle = slide.title || `Slide ${index + 1}`;

            return (
              <button
                key={slide.id}
                onClick={() => onSlideSelect(index)}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                  isCurrent
                    ? 'bg-primary/10 text-primary font-medium border border-primary/20'
                    : isViewed
                    ? 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <div className="flex-shrink-0">
                  {isViewed ? (
                    <CheckCircleIconSolid className="h-4 w-4 text-green-600" />
                  ) : (
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      isCurrent ? 'border-primary bg-primary' : 'border-gray-300'
                    }`} />
                  )}
                </div>
                <span className="text-sm flex-1 truncate">{slideTitle}</span>
                {isCurrent && (
                  <ChevronRightIcon className="h-4 w-4 text-primary flex-shrink-0" />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Progress Summary */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="text-xs text-gray-600">
          <div className="flex items-center justify-between mb-1">
            <span>Progress</span>
            <span className="font-medium">
              {viewedSlides.size} / {slides.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-primary h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${(viewedSlides.size / slides.length) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
